import { get } from '../object'
import { has } from '../object'

import '@formatjs/intl-listformat/polyfill.js'
import '@formatjs/intl-listformat/locale-data/pt.js'

interface ErrorMessageResult {
  status?: number
  statusCode?: number
  code?: string
  name?: string
  title: string
  message: string
  errors?: any
}

/**
 * Processa um erro e retorna uma mensagem de erro formatada
 * @param error Erro a ser processado
 * @param keysNames Mapeamento de chaves para nomes mais amigáveis
 * @param messagesNames Mapeamento de mensagens para nomes mais amigáveis
 * @returns Objeto com informações sobre o erro
 */
export const getErrorMessage = (
  error: any,
  keysNames = {},
  messagesNames = {}
): ErrorMessageResult => {
  if (!error) return { title: '', message: '' }

  let errors = error
  let errorTitle = 'Oops'
  let errorMessage = ''
  const errorStatus = error?.code || get(error, 'response.status', 500)

  // Error 😨
  if (error.response) {
    /*
     * The request was made and the server responded with a
     * status code that falls out of the range of 2xx
     */
    errorTitle = error.response.status

    if (
      has(error.response, 'data.error_message') &&
      error.response.data.error_message
    ) {
      errorTitle = 'Oops'
      errors = error.response.data.error_message
    } else if (has(error.response, 'data.errors'))
      errors = error.response.data.errors
    else if (has(error.response, 'data.error'))
      errors = error.response.data.error
    else if (has(error.response, 'data.erro')) errors = error.response.data.erro
    else if (has(error.response, 'data.message.errors'))
      errors = error.response.data.message.errors
    else if (has(error.response, 'data.message.error'))
      errors = error.response.data.message.error
    else if (has(error.response, 'data.message'))
      errors = error.response.data.message
    else if (has(error.response, 'data')) errors = error.response.data
    else errors = 'Ocorreu um erro inesperado'
  } else if (error.request) {
    /*
     * The request was made but no response was received, `error.request`
     * is an instance of XMLHttpRequest in the browser and an instance
     * of http.ClientRequest in Node.js
     */
    errorTitle = 'Sem internet'
    errors =
      'Sua conexão falhou durante o processo e não conseguimos continuar, verifique e tente novamente.'
  } else if (error.message) {
    // Something happened in setting up the request and triggered an Error
    errors = error.message
  }

  let errorsArray: any[] = []
  if (Array.isArray(errors)) {
    errorsArray = errors.map(error =>
      typeof error === 'object' && error !== null
        ? error.message || error.text || JSON.stringify(error)
        : error
    )
  } else if (typeof errors === 'object' && errors !== null) {
    for (const errorKey of Object.keys(errors)) {
      const errorMessages = errors[errorKey]
      const name = get(keysNames, errorKey, errorKey)

      if (Array.isArray(errorMessages)) {
        try {
          errorsArray.push(
            `${name} ${new Intl.ListFormat('pt-BR').format(errorMessages)}`
          )
        } catch (error) {
          errorsArray.push(`${name} ${JSON.stringify(errorMessages)}`)
        }
      } else {
        errorsArray.push(
          `${name} ${
            typeof errorMessages === 'string'
              ? errorMessages
              : JSON.stringify(errorMessages)
          }`
        )
      }
    }
  } else {
    errorsArray = [errors].map(error => messagesNames[error] || error)
  }

  errorMessage = new Intl.ListFormat('pt-BR').format(errorsArray)

  return {
    status: errorStatus,
    statusCode: errorStatus,
    code: error?.name || errorMessage,
    name: error?.name || errorMessage,
    title: errorTitle.toString(),
    message: errorMessage,
    errors
  }
}
